import React, { useMemo } from 'react';
import { connectMenu } from 'react-contextmenu';

import { FinalCut, RenderStatus } from 'services/finalcut.service';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { Path, useAppNavigator } from 'components/_hooks/use-router';
import { fetchStories, reRenderFinalcut, restoreFcStory, selectStory } from 'components/app/_mediator/app-data.slice';
import { setSelectedStory } from 'components/app/projects/_mediator/projects.slice';

import { ContextMenu, MenuTrigger, MenuOption } from 'components/UI/context-menu/context-menu';

interface MenuData {
  id: string;
  ['data-finalcut']: FinalCut;
}
interface MenuModal {
  id: string;
  trigger: MenuData;
}
interface Props extends React.HTMLAttributes<HTMLDivElement> {
  finalcut: FinalCut;
}
const MENU_ID = 'finalcutMenu';

// C O M P O N E N T S

export const FinalcutMenuContainer = ({ finalcut, children, className, ...rest }: Props) => (
  <MenuTrigger
    id={MENU_ID}
    collect={(props) => props}
    data-finalcut={finalcut}
    attributes={{ className, style: { width: '100%' }, ...rest }}
    holdToDisplay={-1}
  >
    {children}
  </MenuTrigger>
);

const MenuModal = ({ id, trigger }: MenuModal) => {
  const { navigateTo } = useAppNavigator();
  const dispatch = useAppDispatch();
  const selectedStoryData = useAppSelector(selectStory);
  const selectedProject = useAppSelector((state) => state.projects.selectedProject);

  const finalcut = useMemo(() => {
    if (!trigger) return null;
    return trigger['data-finalcut'];
  }, [trigger]);

  const handleRestore = async () => {
    if (!finalcut || !selectedProject) return;
    const payload = {
      finalCutId: finalcut.id,
      storyName: `${selectedStoryData?.name ?? 'storyName'}-${finalcut.name}`,
    };
    const story = await dispatch(restoreFcStory(payload)).unwrap();
    if (!story) return;
    await dispatch(fetchStories(selectedProject));
    dispatch(setSelectedStory(story.storyId));
    navigateTo(Path.projects);
  };

  const handleReRender = async () => {
    if (!finalcut || !selectedProject) return;
    await dispatch(reRenderFinalcut(finalcut.id)).unwrap();
  };

  return (
    <ContextMenu id={id}>
      <MenuOption onClick={handleRestore}>Restore Story</MenuOption>
      <MenuOption disabled={finalcut?.status !== RenderStatus.error} onClick={handleReRender}>
        Re-render Final Cut
      </MenuOption>
    </ContextMenu>
  );
};

export const FinalcutMenu = connectMenu(MENU_ID)(MenuModal);
